import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Play, Search, Download, Loader2, CheckCircle2, XCircle, X, ExternalLink, Sparkles } from 'lucide-react';
import DataTable, { Column } from '../components/DataTable';
import { useTranslation } from 'react-i18next';
import { fetchAIResults, triggerAI } from '../lib/api';
import { useTenant } from '../contexts/TenantContext';
import { useAutoRefresh } from '../contexts/AutoRefreshContext';
import { exportToCSV } from '../lib/export';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface AIResult {
  id: number;
  type: string;
  tenant_id: string;
  provider: string;
  cost_cad: number;
  input_tokens: number;
  output_tokens: number;
  latency_ms: number;
  created_at: string;
}

interface PaginatedResponse {
  items: AIResult[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

interface TriggerResult {
  status: string;
  message: string;
  job_id: string;
  provider: string;
  response: string;
  input_tokens: number;
  output_tokens: number;
  cost_cad: number;
  latency_ms: number;
}

type TriggerPhase = 'idle' | 'submitting' | 'waiting' | 'completed' | 'failed';

export default function AIResultsPage() {
  const [data, setData] = useState<PaginatedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const { selectedTenantId, tenants } = useTenant();
  const { subscribe } = useAutoRefresh();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Trigger state.
  const [showTrigger, setShowTrigger] = useState(false);
  const [triggerTenant, setTriggerTenant] = useState('');
  const [triggerPhase, setTriggerPhase] = useState<TriggerPhase>('idle');
  const [triggerResult, setTriggerResult] = useState<TriggerResult | null>(null);
  const [triggerError, setTriggerError] = useState('');
  const [showReport, setShowReport] = useState(false);

  const filteredItems = useMemo(() => {
    if (!search || !data?.items) return data?.items || [];
    const q = search.toLowerCase();
    return data.items.filter(
      (r) =>
        r.type.toLowerCase().includes(q) ||
        r.provider.toLowerCase().includes(q) ||
        r.tenant_id.toLowerCase().includes(q)
    );
  }, [data?.items, search]);

  const handleExport = () => {
    if (!filteredItems.length) return;
    exportToCSV(
      filteredItems.map((r) => ({
        ...r,
        created_at: r.created_at ? format(new Date(r.created_at), 'yyyy-MM-dd HH:mm') : '',
        cost_cad: r.cost_cad.toFixed(4),
        latency_s: (r.latency_ms / 1000).toFixed(1),
      })),
      [
        { key: 'id', header: 'ID' },
        { key: 'type', header: 'Type' },
        { key: 'tenant_id', header: 'Tenant' },
        { key: 'provider', header: 'Provider' },
        { key: 'cost_cad', header: 'Cost (CAD)' },
        { key: 'input_tokens', header: 'Input Tokens' },
        { key: 'output_tokens', header: 'Output Tokens' },
        { key: 'latency_s', header: 'Latency (s)' },
        { key: 'created_at', header: 'Date' },
      ],
      `ai-results-${format(new Date(), 'yyyy-MM-dd')}`
    );
    toast.success(t('common.exported', { count: filteredItems.length }));
  };

  const loadData = useCallback(() => {
    setLoading(true);
    fetchAIResults({ page, page_size: 20, type: typeFilter || undefined, tenant_id: selectedTenantId || undefined })
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, typeFilter, selectedTenantId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => subscribe(loadData), [subscribe, loadData]);

  const handleTrigger = async () => {
    if (!triggerTenant) {
      toast.error(t('ai.selectTenantError'));
      return;
    }

    setTriggerPhase('submitting');
    setTriggerResult(null);
    setTriggerError('');
    setShowReport(false);

    // Brief delay to show submitting phase.
    await new Promise((r) => setTimeout(r, 300));
    setTriggerPhase('waiting');

    try {
      const result = await triggerAI({ tenant_id: triggerTenant, type: 'periodic' });
      setTriggerResult(result);
      setTriggerPhase('completed');
      setShowReport(true);
      toast.success(t('ai.analysisCompleted'));
      loadData();
    } catch (err: any) {
      setTriggerError(err.response?.data?.error || t('ai.triggerFailed'));
      setTriggerPhase('failed');
      toast.error(err.response?.data?.error || t('ai.triggerFailed'));
    }
  };

  const resetTrigger = () => {
    setTriggerPhase('idle');
    setTriggerResult(null);
    setTriggerError('');
    setShowReport(false);
  };

  const columns: Column<AIResult>[] = [
    { key: 'type', header: t('ai.type') },
    { key: 'tenant_id', header: t('analysis.tenant') },
    { key: 'provider', header: t('ai.provider') },
    {
      key: 'cost_cad',
      header: t('ai.cost'),
      render: (r) => `$${r.cost_cad.toFixed(4)}`,
    },
    {
      key: 'tokens',
      header: t('ai.tokens'),
      render: (r) => `${r.input_tokens} / ${r.output_tokens}`,
      sortable: false,
    },
    {
      key: 'latency_ms',
      header: t('ai.latency'),
      render: (r) => `${(r.latency_ms / 1000).toFixed(1)}s`,
    },
    {
      key: 'created_at',
      header: t('ai.date'),
      render: (r) => r.created_at ? format(new Date(r.created_at), 'MMM d, HH:mm') : '-',
    },
  ];

  const selectClass = "text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200";

  const phaseIndicator = () => {
    if (triggerPhase === 'idle') return null;

    const phases: { key: TriggerPhase; label: string }[] = [
      { key: 'submitting', label: t('ai.submitting') },
      { key: 'waiting', label: t('ai.waitingResult') },
      { key: 'completed', label: t('ai.resultReady') },
    ];

    return (
      <div className="flex items-center gap-6 mt-3">
        {phases.map((phase, i) => {
          const isActive = phase.key === triggerPhase;
          const isPast = phases.findIndex((p) => p.key === triggerPhase) > i;
          const isFailed = triggerPhase === 'failed' && i === phases.findIndex((p) => p.key === 'waiting');

          return (
            <div key={phase.key} className="flex items-center gap-2">
              {isPast ? (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              ) : isActive && triggerPhase !== 'completed' ? (
                <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
              ) : isActive && triggerPhase === 'completed' ? (
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              ) : isFailed ? (
                <XCircle className="w-5 h-5 text-red-500" />
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600" />
              )}
              <span className={`text-sm ${isActive ? 'font-semibold text-gray-900 dark:text-gray-100' : isPast ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                {phase.label}
              </span>
              {i < phases.length - 1 && (
                <div className={`w-8 h-0.5 ${isPast ? 'bg-green-400' : 'bg-gray-200 dark:bg-gray-700'}`} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Parse AI response for display.
  const formatResponse = (text: string) => {
    try {
      const parsed = JSON.parse(text);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return text;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search')}
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          className={selectClass}
        >
          <option value="">{t('ai.allTypes')}</option>
          <option value="nightly_batch">{t('ai.nightlyBatch')}</option>
          <option value="alert_triage">{t('ai.alertTriage')}</option>
          <option value="periodic">{t('ai.periodic')}</option>
          <option value="on_demand">{t('ai.onDemand')}</option>
        </select>
        <button
          onClick={handleExport}
          disabled={!filteredItems.length}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> CSV
        </button>
        <button
          onClick={() => { setShowTrigger(!showTrigger); if (showTrigger) resetTrigger(); }}
          className="ml-auto flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg"
        >
          <Brain className="w-4 h-4" /> {t('ai.triggerAnalysis')}
        </button>
        {data && (
          <p className="text-sm text-gray-500 dark:text-gray-400 w-full sm:w-auto">
            {filteredItems.length}/{data.total} results &middot; Page {data.page}/{data.total_pages}
          </p>
        )}
      </div>

      {/* Trigger panel */}
      {showTrigger && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-4">
            <Sparkles className="w-5 h-5 text-blue-500 shrink-0" />
            <select
              value={triggerTenant}
              onChange={(e) => { setTriggerTenant(e.target.value); resetTrigger(); }}
              className={`${selectClass} flex-1`}
              disabled={triggerPhase === 'submitting' || triggerPhase === 'waiting'}
            >
              <option value="">{t('common.selectTenant')}</option>
              {tenants.map((tn) => (
                <option key={tn.id} value={tn.id}>{tn.name}</option>
              ))}
            </select>
            <button
              onClick={handleTrigger}
              disabled={triggerPhase === 'submitting' || triggerPhase === 'waiting'}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm px-4 py-2 rounded-lg transition-colors"
            >
              {triggerPhase === 'submitting' || triggerPhase === 'waiting' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {triggerPhase === 'submitting' || triggerPhase === 'waiting' ? t('common.running') : t('common.run')}
            </button>
          </div>

          {/* Progress indicator */}
          {phaseIndicator()}

          {/* Error message */}
          {triggerPhase === 'failed' && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <XCircle className="w-5 h-5 text-red-500 shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-400">{triggerError}</p>
              <button onClick={resetTrigger} className="ml-auto text-red-500 hover:text-red-700">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Inline report */}
      {showReport && triggerResult && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-green-200 dark:border-green-800 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">{t('ai.analysisReport')}</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  // Find the latest result and navigate to it.
                  loadData();
                  if (data?.items?.[0]) {
                    navigate(`/ai-results/${data.items[0].id}`);
                  }
                }}
                className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                <ExternalLink className="w-3 h-3" /> {t('ai.viewFullReport')}
              </button>
              <button onClick={() => setShowReport(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 px-5 py-3 border-b border-gray-100 dark:border-gray-700 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">{t('ai.providerLabel')}</span>
              <p className="font-medium text-gray-800 dark:text-gray-200">{triggerResult.provider}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">{t('ai.costLabel')}</span>
              <p className="font-medium text-gray-800 dark:text-gray-200">${triggerResult.cost_cad?.toFixed(4)}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">{t('ai.tokensLabel')}</span>
              <p className="font-medium text-gray-800 dark:text-gray-200">{triggerResult.input_tokens} / {triggerResult.output_tokens}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">{t('ai.latencyLabel')}</span>
              <p className="font-medium text-gray-800 dark:text-gray-200">{(triggerResult.latency_ms / 1000).toFixed(1)}s</p>
            </div>
          </div>

          {/* Response content */}
          <pre className="px-5 py-4 text-sm text-gray-700 dark:text-gray-300 overflow-auto max-h-[500px] whitespace-pre-wrap bg-white dark:bg-gray-900/30">
            {formatResponse(triggerResult.response)}
          </pre>
        </div>
      )}

      {loading ? (
        <div className="animate-pulse bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 h-96" />
      ) : (
        <>
          <DataTable
            data={filteredItems}
            columns={columns}
            keyExtractor={(r) => r.id}
            onRowClick={(r) => navigate(`/ai-results/${r.id}`)}
            emptyMessage={t('ai.noResults')}
          />
          {data && data.total_pages > 1 && (
            <div className="flex justify-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200">{t('common.previous')}</button>
              <button onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))} disabled={page >= data.total_pages}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200">{t('common.next')}</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
